/**
 * Utility functions for image handling and fallbacks
 */

/**
 * Generate a data URL for a placeholder image
 * @param width - Image width
 * @param height - Image height
 * @param text - Text to display
 * @param bgColor - Background color (hex without #)
 * @param textColor - Text color (hex without #)
 * @returns Data URL string
 */
export const generatePlaceholderImage = (
  width: number = 400,
  height: number = 300,
  text: string = 'No Image',
  bgColor: string = 'E5E7EB',
  textColor: string = '9CA3AF'
): string => {
  const svg = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="#${bgColor}"/>
      <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="16" fill="#${textColor}" text-anchor="middle" dominant-baseline="middle">
        ${text}
      </text>
    </svg>
  `;
  
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

/**
 * Generate a car placeholder image
 * @param width - Image width
 * @param height - Image height
 * @returns Data URL string
 */
export const generateCarPlaceholder = (width: number = 400, height: number = 300): string => {
  const svg = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="#F3F4F6"/>
      <g transform="translate(${width/2 - 40}, ${height/2 - 20})">
        <!-- Car icon -->
        <path d="M8 16c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm8 0c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm4-4H4c-1.1 0-2-.9-2-2V8c0-1.1.9-2 2-2h2l2-2h8l2 2h2c1.1 0 2 .9 2 2v2c0 1.1-.9 2-2 2z" 
              fill="#9CA3AF" 
              transform="scale(2.5)"/>
      </g>
      <text x="50%" y="75%" font-family="Arial, sans-serif" font-size="14" fill="#6B7280" text-anchor="middle">
        No Image Available
      </text>
    </svg>
  `;
  
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

/**
 * Generate an error placeholder image
 * @param width - Image width
 * @param height - Image height
 * @returns Data URL string
 */
export const generateErrorPlaceholder = (width: number = 200, height: number = 200): string => {
  const svg = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="#FEF2F2"/>
      <g transform="translate(${width/2 - 12}, ${height/2 - 12})">
        <!-- Error icon -->
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" 
              fill="#EF4444" 
              transform="scale(1)"/>
      </g>
      <text x="50%" y="75%" font-family="Arial, sans-serif" font-size="12" fill="#DC2626" text-anchor="middle">
        Image Error
      </text>
    </svg>
  `;
  
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

/**
 * Handle image error by setting a fallback image
 * @param event - Image error event
 * @param fallbackType - Type of fallback ('car' | 'error')
 * @param width - Image width
 * @param height - Image height
 */
export const handleImageError = (
  event: React.SyntheticEvent<HTMLImageElement>,
  fallbackType: 'car' | 'error' = 'car',
  width?: number,
  height?: number
): void => {
  const target = event.target as HTMLImageElement;
  
  // Prevent infinite loop if fallback also fails
  if (target.src.startsWith('data:image/svg+xml')) {
    return;
  }
  
  const actualWidth = width || target.naturalWidth || target.width || 400;
  const actualHeight = height || target.naturalHeight || target.height || 300;
  
  if (fallbackType === 'error') {
    target.src = generateErrorPlaceholder(actualWidth, actualHeight);
  } else {
    target.src = generateCarPlaceholder(actualWidth, actualHeight);
  }
};
